import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import './styles.scss';

const OptimizedCombobox = ({
    id,
    name,
    label,
    options = [],
    value = '',
    onChange,
    placeholder = 'Seleccionar...',
    isDisabled = false,
    isSearchable = true,
    className = '',
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Encontrar la opción seleccionada usando useMemo para evitar recálculos
    const selectedOption = useMemo(() => 
        options.find(option => option.value === value) || null, 
        [options, value]
    );
    
    const displayValue = useMemo(() => 
        selectedOption ? selectedOption.label : (value || ''), 
        [selectedOption, value]
    );

    // Filtrar opciones basadas en el término de búsqueda usando useMemo
    const filteredOptions = useMemo(() => {
        if (!isSearchable || !searchTerm) {
            return options;
        }
        return options.filter(option =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [options, isSearchable, searchTerm]);

    // Manejar clic fuera del dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Manejar selección de opción usando useCallback
    const handleSelectOption = useCallback((option) => {
        if (isDisabled) return;
        
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        
        // Crear un evento sintético que simule el comportamiento del input
        const syntheticEvent = {
            target: {
                name: name || id,
                value: option.value
            }
        };
        
        // Llamar al onChange con el formato esperado
        if (onChange) {
            onChange(option, syntheticEvent);
        }
    }, [isDisabled, name, id, onChange]);

    // Manejar cambios en el input usando useCallback
    const handleInputChange = useCallback((e) => {
        if (isDisabled) return;
        
        const newValue = e.target.value;
        setSearchTerm(newValue);
        
        if (!isOpen) {
            setIsOpen(true);
        }
        
        // Resetear highlighted index
        setHighlightedIndex(-1);
    }, [isDisabled, isOpen]);

    // Manejar teclado usando useCallback
    const handleKeyDown = useCallback((e) => {
        if (isDisabled) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (!isOpen) {
                    setIsOpen(true);
                } else {
                    setHighlightedIndex(prev => 
                        prev < filteredOptions.length - 1 ? prev + 1 : prev
                    );
                }
                break;
                
            case 'ArrowUp':
                e.preventDefault();
                if (isOpen) {
                    setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                }
                break;
                
            case 'Enter':
                e.preventDefault();
                if (isOpen && highlightedIndex >= 0) {
                    handleSelectOption(filteredOptions[highlightedIndex]);
                } else if (!isOpen) {
                    setIsOpen(true);
                }
                break;
                
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
                inputRef.current?.blur();
                break;
                
            case 'Tab':
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
                break;
        }
    }, [isDisabled, isOpen, highlightedIndex, filteredOptions, handleSelectOption]);

    // Manejar focus en el input usando useCallback
    const handleInputFocus = useCallback(() => {
        if (!isDisabled) {
            setIsOpen(true);
        }
    }, [isDisabled]);

    // Manejar clic en el input usando useCallback
    const handleInputClick = useCallback(() => {
        if (!isDisabled && !isOpen) {
            setIsOpen(true);
        }
    }, [isDisabled, isOpen]);

    // Manejar clic en la flecha usando useCallback
    const handleArrowClick = useCallback(() => {
        if (!isDisabled) {
            setIsOpen(!isOpen);
        }
    }, [isDisabled, isOpen]);

    // Manejar mouse enter en opción usando useCallback
    const handleOptionMouseEnter = useCallback((index) => {
        setHighlightedIndex(index);
    }, []);

    return (
        <div className={`optimized-combobox ${className}`}>
            {label && <label htmlFor={id}>{label}</label>}
            <div className="combobox-wrapper" ref={dropdownRef}>
                <input
                    ref={inputRef}
                    id={id}
                    name={name || id}
                    type="text"
                    value={isOpen ? searchTerm : displayValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleInputFocus}
                    onClick={handleInputClick}
                    placeholder={placeholder}
                    disabled={isDisabled}
                    className="combobox-input"
                    autoComplete="off"
                    {...props}
                />
                <div className="combobox-arrow" onClick={handleArrowClick}>
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
                
                {isOpen && !isDisabled && (
                    <div className="combobox-dropdown">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={option.value}
                                    className={`combobox-option ${index === highlightedIndex ? 'highlighted' : ''} ${option.value === value ? 'selected' : ''}`}
                                    onClick={() => handleSelectOption(option)}
                                    onMouseEnter={() => handleOptionMouseEnter(index)}
                                >
                                    {option.label}
                                </div>
                            ))
                        ) : (
                            <div className="combobox-no-options">
                                No hay opciones disponibles
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OptimizedCombobox;
